import { useMemo } from 'react';
import { useMakeSelector } from 'hooks/use-make-selector';
import { usePodClickTracking } from 'hooks/use-pod-click-tracking';
import useTrolleyActions from 'hooks/use-trolley-actions';
import { useDispatch, useSelector } from 'react-redux';
import { makeGetIsFavourite } from 'redux/modules/favourites-products/selectors';
import { addRemoveFavourite } from 'redux/modules/search-and-browse/actions/add-remove-favourite';
import { getIsRedPriceProduct } from 'redux/modules/entities/selectors/promotions';
import { getPageType } from 'utils/get-page-type';
import { getLocation } from 'redux/modules/routing/selectors';
import { useDecisionById } from 'components/Experiment/useDecisionById';
import { EOS_OFFERS_TEXT } from 'constants/monetateExperiments';
import { makeGetDSConflict } from './selectors/selectConflict';
import { makeGetDSProduct } from './selectors/selectProduct';
import { updateTrolleyParams } from './utils';

// this returns object that conforms to props type of Design System (DS) pod
// @johnlewispartnership/wtr-ingredients/ingredients/ProductPod

export function useDSPodProps({ lineNumber, position, sponsored, clickContext }) {
  const dispatch = useDispatch();
  const wrapperId = getPageType(useSelector(getLocation)?.pathname);
  const isFavourite = useMakeSelector(makeGetIsFavourite, lineNumber);
  const { product: initialProduct, productData } = useMakeSelector(makeGetDSProduct, lineNumber);
  const { variant, payload: promotionsIds } = useDecisionById(EOS_OFFERS_TEXT.experiment);

  const promotionsWithExperiments = useMemo(
    () =>
      (initialProduct.promotions || []).map(promotion => {
        const { promotionId, promotionDescription } = promotion;

        let descriptionWithExperiment = promotionDescription;

        if (promotionId && promotionsIds?.includes(promotionId)) {
          switch (variant) {
            case EOS_OFFERS_TEXT.variant.phase1:
              descriptionWithExperiment = `Any${promotionDescription.substring(3)}`;
              break;
            case EOS_OFFERS_TEXT.variant.phase2:
              descriptionWithExperiment = `Any${promotionDescription.substring(3)} Mix & Match`;
              break;
            default:
              break;
          }
        }
        return { ...promotion, promotionDescription: descriptionWithExperiment };
      }),
    [initialProduct.promotions, promotionsIds, variant],
  );

  const product = useMemo(
    () => ({
      ...initialProduct,
      ...(promotionsWithExperiments.length ? { promotions: promotionsWithExperiments } : {}),
    }),
    [initialProduct, promotionsWithExperiments],
  );

  const redOfferText = useMakeSelector(() => getIsRedPriceProduct, lineNumber);
  const {
    addToTrolley,
    updateInTrolley,
    optimisticTrolleyQuantityAmount: amount,
    optimisticTrolleyQuantityUom: uomId = product.uoms.item,
  } = useTrolleyActions({ lineNumber, productId: product.productId });
  const conflict = useMakeSelector(makeGetDSConflict, product.productId, amount > 0, {
    isFavourite,
    position,
    sponsored,
    wrapperId,
    boosted: false,
  });
  const {
    reportClickToAdTech,
    reportAnalyticsClick,
    reportClickToDataLayer,
    reportClickToMonetate,
  } = usePodClickTracking({
    conflictMessage: conflict?.message?.toLowerCase(),
    isFavourite,
    position,
    productId: product.productId,
    wrapperId,
    boosted: false,
    sponsored,
  });

  // conform to type @johnlewispartnership/wtr-ingredients/ingredients/ProductPod/types.ts#Product
  product.sponsored = sponsored;
  product.favourite = isFavourite;
  product.productLinkProps.onClick = reportAnalyticsClick;

  // handlers

  const trolleyCtx = { productId: product.productId, lineNumber, position, clickContext };

  const onAddToTrolley = (q, e) => {
    addToTrolley(updateTrolleyParams(q, e, trolleyCtx));
    reportAnalyticsClick();
  };
  const onUpdateTrolleyQuantity = (q, e) => {
    updateInTrolley(updateTrolleyParams(q, e, trolleyCtx, 'newQuantity'));
    reportClickToAdTech();
  };

  const onFavouriteClick = () => {
    if (!isFavourite) reportClickToAdTech();
    reportClickToDataLayer();
    reportClickToMonetate();
    dispatch(addRemoveFavourite(isFavourite, lineNumber));
  };

  if (conflict?.resolution?.type === 'button') {
    conflict.resolution.action.onClick = e => onUpdateTrolleyQuantity({ amount: 0, uomId }, e);
  }

  return {
    product,
    conflict,
    trolleyQuantity: { amount, uomId },
    onFavouriteClick,
    onAddToTrolley,
    onUpdateTrolleyQuantity,
    productData,
    redOfferText,
  };
}
